import { datadogLogs } from '@datadog/browser-logs';
import { skipToken } from '@reduxjs/toolkit/query';
import { FormSection } from '@strata/react-form-section';
import { IntakeCaseTimeline, ReferralTimeline } from '@strata/react-timeline';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LiveReferralDetails } from 'components/referrals/LiveReferralDetails';
import { MemberDetails } from 'components/referrals/MemberDetails';
import { PrescriberDetails } from 'components/referrals/PrescriberDetails';
import { ReferralDetails } from 'components/referrals/ReferralDetails';
import { getReferralInlineMessages } from 'components/referrals/ReferralInlineMessages';
import { SupplierDetails } from 'components/referrals/SupplierDetails';
import { useIntakeCaseMilestones } from 'services/intakeCaseHelpers';
import { useEnrichedReferralMilestones } from 'services/milestoneHelpers';
import { useGetPrescriberFaxPrefQuery, useGetReferralFaxStatusQuery } from 'store/api/clientApi';
import { useGetPrescriberDetailsQuery } from 'store/api/referenceApi';
import { fetchReferralDetails } from 'store/referrals/referralsSlice';

export const ReferralDetailsOverview = ({
    payerId,
    referralId,
    showMemberDetails = false,
    showInlineMessages = true,
    intakeCaseMilestoneReferralFields = undefined,
}) => {
    const dispatch = useDispatch();
    const { showIntakeTimelineAgora } = useFlags();

    const referralDetails = useSelector((state) => state.referrals.referralDetails[referralId]);
    useEffect(() => {
        if (!referralDetails) {
            dispatch(fetchReferralDetails({ payerId, referralId }));
        }
    }, [dispatch, payerId, referralDetails, referralId]);

    const { data: prescriberDetails, isLoading: isPrescriberDetailsLoading } =
        useGetPrescriberDetailsQuery(
            { prescriberNpi: referralDetails?.prescriberNpi },
            { skip: !referralDetails?.prescriberNpi },
        );
    const { data: prescriberFaxPref } = useGetPrescriberFaxPrefQuery(
        {
            payerId: referralDetails?.payerId,
            segmentId: referralDetails?.segmentId,
        },
        { skip: !referralDetails },
    );
    const { data: referralFaxStatus } = useGetReferralFaxStatusQuery(
        {
            payerId: referralDetails?.payerId,
            referralId: referralDetails?.referralId,
        },
        { skip: !referralDetails },
    );

    const { enrichedMilestones, milestonesFetching: referralMilestonesFetching } =
        useEnrichedReferralMilestones({
            payerId,
            referralId,
            referralDetails,
        });

    const {
        milestones,
        hasMilestones,
        milestonesFetching: intakeMilestonesFetching,
        milestonesLoading: intakeMilestonesLoading,
        milestonesUninitialized: intakeMilestonesUninitialized,
        milestonesIsError: intakeMilestonesIsError,
        milestonesQueryProps,
    } = useIntakeCaseMilestones({
        referralDetails: intakeCaseMilestoneReferralFields,
        showIntakeTimeline: showIntakeTimelineAgora,
    });

    const shouldRenderIntakeTimeline = showIntakeTimelineAgora && hasMilestones;

    const intakeMilestonesQueryKey = useMemo(
        () => (milestonesQueryProps === skipToken ? 'skip' : JSON.stringify(milestonesQueryProps)),
        [milestonesQueryProps],
    );

    useEffect(() => {
        datadogLogs.logger.info('intake_milestones_overview_state', {
            referral_id: referralId,
            ld_show_intake_timeline: String(showIntakeTimelineAgora),
            intake_fields_present: String(intakeCaseMilestoneReferralFields != null),
            query_key: intakeMilestonesQueryKey,
            rtk_data_is_array: String(Array.isArray(milestones)),
            rtk_data_length: milestones == null ? 'none' : String(milestones.length),
            has_milestones: String(hasMilestones),
            should_render_prior_auth_timeline: String(shouldRenderIntakeTimeline),
            is_fetching: String(intakeMilestonesFetching),
            is_loading: String(intakeMilestonesLoading),
            is_uninitialized: String(intakeMilestonesUninitialized),
            is_error: String(intakeMilestonesIsError),
            timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
        });
    }, [
        referralId,
        showIntakeTimelineAgora,
        intakeCaseMilestoneReferralFields,
        intakeMilestonesQueryKey,
        milestones,
        hasMilestones,
        shouldRenderIntakeTimeline,
        intakeMilestonesFetching,
        intakeMilestonesLoading,
        intakeMilestonesUninitialized,
        intakeMilestonesIsError,
    ]);

    return (
        <div className="sds_details-page-overview">
            <div className="sds_details-page-overview__container" style={{ flex: '2 0' }}>
                {showInlineMessages &&
                    getReferralInlineMessages({ referralDetails, enrichedMilestones })}
                <ReferralDetails referralDetails={referralDetails} />
                {showMemberDetails && <MemberDetails referralDetails={referralDetails} />}
                <LiveReferralDetails referralDetails={referralDetails} />
                <div className="flex__gap--medium">
                    <PrescriberDetails
                        prescriberDetails={prescriberDetails}
                        prescriberFaxPref={prescriberFaxPref}
                        referralFaxStatus={referralFaxStatus}
                        isLoading={isPrescriberDetailsLoading}
                    />
                    <SupplierDetails
                        referralDetails={referralDetails}
                        referralFaxStatus={referralFaxStatus}
                    />
                </div>
            </div>
            <div className="sds_details-page-overview__container" style={{ flex: '1 0' }}>
                {shouldRenderIntakeTimeline && (
                    <FormSection
                        sectionTitle="Intake Case Timeline"
                        isLoading={intakeMilestonesFetching}
                        content={<IntakeCaseTimeline milestones={milestones} />}
                    />
                )}
                <FormSection
                    sectionTitle="Referral Timeline"
                    isLoading={referralMilestonesFetching}
                    content={<ReferralTimeline referralMilestones={enrichedMilestones} />}
                />
            </div>
        </div>
    );
};

ReferralDetailsOverview.propTypes = {
    payerId: PropTypes.string.isRequired,
    referralId: PropTypes.string.isRequired,
    showMemberDetails: PropTypes.bool,
    showInlineMessages: PropTypes.bool,
    intakeCaseMilestoneReferralFields: PropTypes.shape({
        segmentId: PropTypes.string,
        memberId: PropTypes.string,
        patientFirstName: PropTypes.string,
        patientDateOfBirth: PropTypes.string,
        productServiceOid: PropTypes.string,
        receivedTime: PropTypes.string,
        endDate: PropTypes.string,
    }),
};
